import React, { useState, useEffect } from 'react';
import {
  Users,
  Building2,
  UserCheck,
  Activity,
  TrendingUp,
  Clock
} from 'lucide-react';
import { useDarkMode } from '../../../contexts/DarkModeContext';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../../firebase';

const StatCard = ({ title, value, icon: Icon, trend, isDarkMode }) => (
  <div className={`p-6 rounded-[24px] border transition-all duration-200 ${
    isDarkMode
      ? 'bg-[#111111] border-gray-800'
      : 'bg-white border-gray-100'
  }`}>
    <div className="flex items-start justify-between mb-4">
      <div className={`p-3 rounded-xl ${
        isDarkMode ? 'bg-emerald-500/20' : 'bg-emerald-100'
      }`}>
        <Icon className={`w-6 h-6 ${
          isDarkMode ? 'text-emerald-400' : 'text-emerald-600'
        }`} />
      </div>
      {trend && (
        <div className={`flex items-center gap-1 text-sm ${
          trend > 0
            ? isDarkMode ? 'text-green-400' : 'text-green-600'
            : isDarkMode ? 'text-red-400' : 'text-red-600'
        }`}>
          <TrendingUp className="w-4 h-4" />
          <span>{Math.abs(trend)}%</span>
        </div>
      )}
    </div>
    <p className={`text-sm mb-1 ${
      isDarkMode ? 'text-gray-400' : 'text-gray-600'
    }`}>
      {title}
    </p>
    <p className={`text-3xl font-bold ${
      isDarkMode ? 'text-white' : 'text-gray-900'
    }`}>
      {value}
    </p>
  </div>
);

const ActivityItem = ({ activity, isDarkMode }) => (
  <div className={`flex items-start gap-3 p-3 rounded-xl ${
    isDarkMode ? 'hover:bg-[#1A1A1A]' : 'hover:bg-gray-50'
  }`}>
    <div className={`p-2 rounded-lg ${
      isDarkMode ? 'bg-blue-500/20' : 'bg-blue-100'
    }`}>
      <Activity className={`w-4 h-4 ${
        isDarkMode ? 'text-blue-400' : 'text-blue-600'
      }`} />
    </div>
    <div className="flex-1 min-w-0">
      <p className={`text-sm ${
        isDarkMode ? 'text-white' : 'text-gray-900'
      }`}>
        {activity.user} requested mosque admin access
      </p>
      <p className={`text-xs mt-1 ${
        isDarkMode ? 'text-gray-500' : 'text-gray-600'
      }`}>
        {new Date(activity.timestamp).toLocaleString()}
      </p>
    </div>
    <span className={`text-xs px-2 py-1 rounded-full ${
      activity.status === 'pending'
        ? isDarkMode ? 'bg-yellow-900/30 text-yellow-400' : 'bg-yellow-100 text-yellow-700'
        : isDarkMode ? 'bg-green-900/30 text-green-400' : 'bg-green-100 text-green-700'
    }`}>
      {activity.status}
    </span>
  </div>
);

const OverviewPage = () => {
  const { isDarkMode } = useDarkMode();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadDashboardStats();
  }, []);

  const loadDashboardStats = async () => {
    try {
      setLoading(true);
      const getDashboardStats = httpsCallable(functions, 'getDashboardStats');
      const result = await getDashboardStats();
      setStats(result.data);
    } catch (error) {
      console.error('Error loading dashboard stats:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`p-6 rounded-xl border ${
        isDarkMode ? 'bg-red-900/20 border-red-500/30 text-red-300' : 'bg-red-50 border-red-200 text-red-700'
      }`}>
        Error loading dashboard: {error}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className={`text-3xl font-bold ${
          isDarkMode ? 'text-white' : 'text-gray-900'
        }`}>
          Dashboard Overview
        </h1>
        <p className={`mt-2 ${
          isDarkMode ? 'text-gray-400' : 'text-gray-600'
        }`}>
          Welcome back! Here's what's happening with your platform.
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Total Users"
          value={stats.users.total.toLocaleString()}
          icon={Users}
          trend={stats.users.growth}
          isDarkMode={isDarkMode}
        />
        <StatCard
          title="Active Users (30d)"
          value={stats.users.active.toLocaleString()}
          icon={Activity}
          isDarkMode={isDarkMode}
        />
        <StatCard
          title="Total Mosques"
          value={stats.mosques.total}
          icon={Building2}
          isDarkMode={isDarkMode}
        />
        <StatCard
          title="Pending Admins"
          value={stats.mosqueAdmins.pending}
          icon={UserCheck}
          isDarkMode={isDarkMode}
        />
      </div>

      {/* Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Activity */}
        <div className={`p-6 rounded-[24px] border ${
          isDarkMode
            ? 'bg-[#111111] border-gray-800'
            : 'bg-white border-gray-100'
        }`}>
          <div className="flex items-center gap-3 mb-6">
            <Clock className={`w-5 h-5 ${
              isDarkMode ? 'text-blue-400' : 'text-blue-600'
            }`} />
            <h2 className={`text-xl font-semibold ${
              isDarkMode ? 'text-white' : 'text-gray-900'
            }`}>
              Recent Activity
            </h2>
          </div>
          <div className="space-y-2">
            {stats.recentActivity.length > 0 ? (
              stats.recentActivity.map((activity, index) => (
                <ActivityItem
                  key={index}
                  activity={activity}
                  isDarkMode={isDarkMode}
                />
              ))
            ) : (
              <p className={`text-sm text-center py-8 ${
                isDarkMode ? 'text-gray-500' : 'text-gray-400'
              }`}>
                No recent activity
              </p>
            )}
          </div>
        </div>

        {/* Quick Stats */}
        <div className={`p-6 rounded-[24px] border ${
          isDarkMode
            ? 'bg-[#111111] border-gray-800'
            : 'bg-white border-gray-100'
        }`}>
          <h2 className={`text-xl font-semibold mb-6 ${
            isDarkMode ? 'text-white' : 'text-gray-900'
          }`}>
            Today's Activity
          </h2>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>
                New Signups
              </span>
              <span className={`font-semibold ${
                isDarkMode ? 'text-white' : 'text-gray-900'
              }`}>
                {stats.activity.signupsToday}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>
                Admin Requests
              </span>
              <span className={`font-semibold ${
                isDarkMode ? 'text-white' : 'text-gray-900'
              }`}>
                {stats.activity.mosqueAdminRequestsToday}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>
                Active Mosques
              </span>
              <span className={`font-semibold ${
                isDarkMode ? 'text-white' : 'text-gray-900'
              }`}>
                {stats.mosques.active}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>
                Mosques with Prayer Times
              </span>
              <span className={`font-semibold ${
                isDarkMode ? 'text-white' : 'text-gray-900'
              }`}>
                {stats.mosques.withPrayerTimes}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OverviewPage;
